import React, { useState } from 'react';
import Button from '@welldev-group/rcl-button';
import SelectBox from '@welldev-group/rcl-select-box';
import { I18nText, Spinner, CheckBox } from '@welldev-group/react-comp-lib';
import Alert from '@welldev-group/rcl-alert';
import Icon from '@welldev-group/rcl-icon';
import Modal from '@welldev-group/rcl-modal';
import PropTypes from 'prop-types';
import SpinnerIcon from 'affiliateIcons/spinner.svg';
import { debounce } from 'throttle-debounce';
import routes from '../../../shared/routes';
import httpClient from '../../../shared/libraries/httpClient';
import ChannelSpecificForm from './ChannelSpecificForm';

import selectBoxObjectMapper from '../../../shared/helper/selectBoxObjectMapper';

const ChannelCreateModal = ({
  workspaceOptions,
  supplierOptions,
  fetchCredentials,
  affiliateId,
  supplierSpecific,
  setIdentifierOnChange,
  identifier,
  setForFlightOnChange,
  forFlight,
  setForHotelOnChange,
  forHotel,
  setForCarOnChange,
  forCar,
  setSupplierParamsOnChange,
  supplierParams,
  enabled,
  setEnabled,
  clearStateOnModalClose,
  isSupplierSelected,
  setSupplierSpecificAttributesOnFetch,
  isFetching,
  setIsFetchingOnChange,
  modalOpen,
  modalClose,
}) => {
  const [workspace, setWorkspace] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [error, setError] = useState({});
  const [createDisable, setCreateDisable] = useState(false);
  const [updatedOptions, setUpdatedOptions] = useState([]);
  const [cityId, setCityId] = useState(null);

  const fetchSupplierSpecificFormAttributes = async selectedSupplier => {
    setIsFetchingOnChange(true);
    const { data } = await httpClient.get(
      routes.credentials.new({
        supplier: selectedSupplier,
        affiliateId,
      }),
    );
    setSupplierSpecificAttributesOnFetch(data);
    setError({});
  };

  const clearCurrentState = () => {
    setWorkspace(null);
    setSupplier(null);
    setError({});
    setCreateDisable(false);
  };

  const onModalClose = () => {
    clearStateOnModalClose();
    clearCurrentState();
    modalClose();
  };

  const onChangeSupplier = value => {
    setSupplier(value);
    fetchSupplierSpecificFormAttributes(value);
  };

  const createCredential = params => {
    httpClient
      .post(routes.credentials.create({ supplier, affiliateId }), params)
      .then(({ data }) => {
        if (data.error === null) {
          fetchCredentials(affiliateId);
          onModalClose();
        }
      })
      .catch(({ response }) => {
        if (response.status === 422) {
          setError(response.data.error);
        }
        setCreateDisable(false);
      });
  };

  const onChangeCityId = option => {
    setCityId(option.value);
  };

  const loadCities = async (inputValue = '') => {
    if (inputValue.length >= 2) {
      const { data } = await httpClient.get(
        routes.cities.list({
          city_name: inputValue,
        }),
      );
      return data;
    }
    return null;
  };

  const onSearchCities = debounce(1000, false, async (inputValue, callback) => {
    const options = await loadCities(inputValue);
    setUpdatedOptions(options);

    if (options && options.length > 0) {
      callback(options);
    }
  });

  const loadOptions = (inputValue, callback) => {
    onSearchCities(inputValue, callback);
  };

  const handleCreateCredential = () => {
    const params = {};
    supplierParams.forEach(param => {
      params[param.key] = param.value;
    });
    params.type = supplierSpecific.type;
    params.supplier = supplier;
    params.workspace_id = workspace;
    params.enabled = enabled;
    params.account_identifier = identifier;
    params.city_identifier = cityId;

    if (supplierSpecific.isFilteringEnabled) {
      params.flight = forFlight ? 1 : 0;
      params.hotel = forHotel ? 1 : 0;
      params.car = forCar ? 1 : 0;
    }
    const credentialParams = {};
    credentialParams[supplierSpecific.formNamespace] = params;
    setCreateDisable(true);
    createCredential(credentialParams);
  };

  return (
    <div className="channel-modal__container">
      <Modal
        className="channel-modal__wrapper modal__wrapper modal__wrapper-small modal__wrapper-overflow modal__wrapper-overflow-vertical"
        isVisible={modalOpen}
        onOutsideClick={modalClose}
      >
        <>
          <div className="modal__header">
            <Modal.Title>
              <I18nText id="channel.create_modal.header" />
            </Modal.Title>
            <div
              onClick={modalClose}
              onKeyDown={modalClose}
              role="presentation"
            >
              <Icon name="close" size="normal" />
            </div>
          </div>
          <Modal.Content>
            <div className="col-12 col-bleed">
              <div className="col-12 col-bleed-x channel-modal__fields">
                <SelectBox
                  placeholderText={I18n.t(
                    'components.channel.placeholder.workspace',
                  )}
                  value={selectBoxObjectMapper(workspaceOptions, workspace)}
                  options={workspaceOptions}
                  onChange={option => setWorkspace(option.value)}
                  isClearable={false}
                  width="full"
                />
              </div>
              <div className="col-12 col-bleed-x channel-modal__fields">
                <SelectBox
                  placeholderText={I18n.t(
                    'components.channel.placeholder.supplier',
                  )}
                  value={selectBoxObjectMapper(supplierOptions, supplier)}
                  options={supplierOptions}
                  onChange={option => onChangeSupplier(option.value)}
                  isClearable={false}
                  width="full"
                />
              </div>
              <div className="col-12 col-bleed-x channel-modal__fields">
                <SelectBox
                  isAsync={true}
                  loadOptions={loadOptions}
                  label={<I18nText id="channel.label.pos_city" />}
                  value={selectBoxObjectMapper(updatedOptions, cityId)}
                  required={true}
                  onChange={onChangeCityId}
                  isClearable={false}
                  cacheOptions={true}
                  width="full"
                  errorMsg={error.cityIdentifier}
                />
              </div>
              <div className="col-12 col-bleed-x channel-modal__fields">
                <CheckBox
                  label={<I18nText id="channel.label.enable_channel" />}
                  onChange={event => setEnabled(event.target.checked)}
                  checked={enabled}
                />
              </div>

              {isSupplierSelected && supplierSpecific ? (
                <ChannelSpecificForm
                  supplierSpecific={supplierSpecific}
                  setIdentifierOnChange={setIdentifierOnChange}
                  identifier={identifier}
                  setForFlightOnChange={setForFlightOnChange}
                  forFlight={forFlight}
                  setForHotelOnChange={setForHotelOnChange}
                  forHotel={forHotel}
                  setForCarOnChange={setForCarOnChange}
                  forCar={forCar}
                  setSupplierParamsOnChange={setSupplierParamsOnChange}
                  supplierParams={supplierParams}
                  error={error}
                />
              ) : (
                isFetching && (
                  <div className="col-12 channel-modal__loading-spinner">
                    <Spinner color="black" size="tiny" />
                  </div>
                )
              )}

              <Alert isVisible={!!error.base} hideClose={true} type="danger">
                {error.base}
              </Alert>
            </div>
          </Modal.Content>
          <Modal.Footer>
            <div className="col-12 col-bleed-x channel-modal__fields">
              <Button
                onClick={handleCreateCredential}
                label={
                  createDisable ? (
                    <>
                      <I18nText id="channel.label.adding" />
                      <img
                        src={SpinnerIcon}
                        alt="spinner"
                        className="channel-modal__spinner"
                      />
                    </>
                  ) : (
                    <I18nText id="channel.label.add" />
                  )
                }
                version="v2"
                size="small"
                type="primary"
                disabled={createDisable || !supplierSpecific}
              />

              <Button
                onClick={onModalClose}
                label={<I18nText id="channel.label.cancel" />}
                version="v2"
                size="small"
              />
            </div>
          </Modal.Footer>
        </>
      </Modal>
    </div>
  );
};

ChannelCreateModal.defaultProps = {
  supplierSpecific: null,
  forFlight: null,
  forHotel: null,
  forCar: null,
  identifier: null,
  supplierParams: null,
};

ChannelCreateModal.propTypes = {
  fetchCredentials: PropTypes.func.isRequired,
  affiliateId: PropTypes.number.isRequired,
  supplierOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  workspaceOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  supplierSpecific: PropTypes.shape({
    accountIdentifier: PropTypes.string,
    forCar: PropTypes.bool,
    forFlight: PropTypes.bool,
    forHotel: PropTypes.bool,
    formNamespace: PropTypes.string,
    isFilteringEnabled: PropTypes.bool,
    type: PropTypes.string,
  }),
  setIdentifierOnChange: PropTypes.func.isRequired,
  identifier: PropTypes.string,
  setForFlightOnChange: PropTypes.func.isRequired,
  forFlight: PropTypes.bool,
  setForHotelOnChange: PropTypes.func.isRequired,
  forHotel: PropTypes.bool,
  setForCarOnChange: PropTypes.func.isRequired,
  forCar: PropTypes.bool,
  setSupplierParamsOnChange: PropTypes.func.isRequired,
  supplierParams: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.number.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  clearStateOnModalClose: PropTypes.func.isRequired,
  isSupplierSelected: PropTypes.bool.isRequired,
  setSupplierSpecificAttributesOnFetch: PropTypes.func.isRequired,
  setIsFetchingOnChange: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  enabled: PropTypes.bool.isRequired,
  setEnabled: PropTypes.func.isRequired,
  modalOpen: PropTypes.bool.isRequired,
  modalClose: PropTypes.func.isRequired,
};

export default ChannelCreateModal;
